import { createContext, useContext } from 'react'

import type { TileChatRequest } from 'features/ask-yogi-anywhere/types/tile-chat-request'
import type { SkillContext } from 'features/ask-yogi/service'
import type { AgentAnnotationArtifact } from 'features/ask-yogi/types'
import type { HandedOffChart } from 'features/charts-v2/types/chart-handoff'
import type { ChartPageContext } from 'features/charts-v2/types/chart-page-context'
import type { PendingChartWrite } from 'features/charts-v2/types/chart-update'
import type {
  DashboardContextBuilder,
  DashboardFocusChip,
} from 'features/dashboards-v2-1/types/dashboard-page-context'
import type { PendingDashboardWrite } from 'features/dashboards-v2-1/types/dashboard-page-write'
import type { PageSkills } from 'features/project/page-skills'

export interface AskYogiAnywhereContextType {
  isAskYogiAnywhereOpen: boolean
  setIsAskYogiAnywhereOpen: (open: boolean) => void
  toggleAskYogiAnywhere: () => void
  benchmarkingInsightUuid: string | null
  benchmarkingInsightEntityName: string | null
  benchmarkingInitialQuestion: string | null
  openForBenchmarking: (
    insightUuid: string,
    entityName?: string,
    initialQuestion?: string,
  ) => void
  exitBenchmarkingMode: () => void
  wikiProjectUuid: string | null
  openForWiki: (projectUuid: string) => void
  exitWikiMode: () => void
  /**
   * The charts the user is currently looking at — every panel, the active
   * one, and the sync toggles — when they are on the charts page. Null
   * everywhere else.
   *
   * Ambient rather than a mode: the benchmarking and wiki entries above are
   * *entered*, and the widget opens into them. This is just true while the
   * charts page is mounted, so the widget reads it without being opened any
   * differently. `isOnChartsPage`, not this, is the surface signal — see
   * `chartContextFor`.
   */
  chartPage: ChartPageContext | null
  setChartPage: (chartPage: ChartPageContext | null) => void
  isOnChartsPage: boolean
  setIsOnChartsPage: (onChartsPage: boolean) => void
  /**
   * True while an editable dashboard with no tiles is on screen. Ambient, like
   * `isOnChartsPage`: the sidebar's welcome screen reads it to swap in the
   * "build a dashboard" prompts, because the pathname alone cannot tell an
   * empty dashboard from a populated one.
   */
  isOnEmptyDashboard: boolean
  setIsOnEmptyDashboard: (onEmptyDashboard: boolean) => void
  /**
   * Which chart the charts page just received via a "View in charts"
   * handoff, and is still showing — see `HandedOffChart`. Null once the
   * charts-page visit ends. Read by the widget's message renderer to
   * collapse that one chart into a chip (PROD-5795).
   */
  handedOffChart: HandedOffChart | null
  /** Increments when a charts-page visit ends. See `PendingChartWrite`. */
  chartPageSession: number
  /**
   * Charts Ask Yogi wrote in the turn that just came back, waiting for the
   * charts page to apply them. Null the rest of the time.
   *
   * The return leg of `chartPage`, and the reason both live here: the widget
   * floats in a different subtree from the page, so the context is the only
   * thing they share. The widget never touches the charts store itself —
   * charts-v2 stays the only writer of its own state.
   *
   * Set ONLY from a live response. A conversation being re-read must not push
   * a month-old chart onto the page the user is working on, which is why this
   * is a slot the widget fills on success rather than something derived from
   * the message list.
   */
  chartUpdate: PendingChartWrite | null
  setChartUpdate: (update: PendingChartWrite | null) => void
  /**
   * Emptied by the page once it has applied the chart.
   *
   * A named clear rather than `setChartUpdate(null)` at the call site: the
   * consumer's effect depends on this function, and a stable identity is what
   * keeps applying a chart from re-running the effect that applied it.
   */
  clearChartUpdate: () => void
  /**
   * Hands the 2.1 dashboard what Ask Yogi wrote to it this turn, with the
   * stamp of the editor state the message was sent from. Set ONLY from a
   * live response, for the reason `chartUpdate` gives; the dashboard reads
   * it through `useDashboardPageWrite`.
   */
  setDashboardWrite: (write: PendingDashboardWrite | null) => void
  /**
   * True while a turn is in flight, so a page showing a chart the agent may be
   * about to change can say so.
   *
   * Not the same as the widget being open, and not the same as it being busy
   * with anything: this is specifically "an answer is coming that might redraw
   * what you are looking at".
   */
  isAskYogiWorking: boolean
  setIsAskYogiWorking: (working: boolean) => void
  /**
   * The conversation whose turn is in flight, or null. A dashboard tile reads
   * it against its own conversation so it says "Making changes…" for a turn
   * sent from the tile OR from the side panel. While a tile's FIRST
   * conversation is still being created there is no uuid yet, so this holds
   * the tile's own id instead.
   */
  workingConversationId: string | null
  setWorkingConversationId: (conversationId: string | null) => void
  /**
   * A question a dashboard tile wants sent, waiting for the widget's hook to
   * pick it up. The tile has no conversation of its own — the widget owns the
   * only one — so it hands the question over through this slot, the same way
   * the charts page receives `chartUpdate`.
   */
  tileChatRequest: TileChatRequest | null
  sendTileChat: (request: TileChatRequest) => void
  /** Stable, for the same reason as `clearChartUpdate`. */
  clearTileChatRequest: () => void
  /**
   * Which conversation each tile has started this session (tile id →
   * conversation uuid), so reopening a tile resumes its conversation.
   */
  tileConversations: Record<string, string>
  setTileConversation: (tileId: string, conversationId: string) => void
  /**
   * The builder the 2.1 dashboard on screen registered, or null. Read at send
   * time so `dashboard_context` describes what is on screen when the message
   * goes. Stable; registering never re-renders this context's consumers.
   */
  getDashboardContextBuilder: () => DashboardContextBuilder | null
  /**
   * The skills the page on screen registered, sent with every turn. Null on
   * pages that register none. See `useRegisterPageSkills`.
   */
  pageSkills: PageSkills | null
  /**
   * Hands what a live turn saved, and what it changed on the page without
   * saving, back to the page, with the skill context the turn was sent with.
   * Called only from a live answer, never for a reloaded conversation — the
   * same rule as `chartUpdate`.
   */
  reportSavedResources: (
    annotations: AgentAnnotationArtifact[],
    skillContext: SkillContext | undefined,
    pageWrites?: Record<string, Record<string, unknown>>,
  ) => void
}

export const AskYogiAnywhereContext = createContext<
  AskYogiAnywhereContextType | undefined
>(undefined)

const noop = (): void => {}

const defaultAskYogiAnywhereContext: AskYogiAnywhereContextType = {
  isAskYogiAnywhereOpen: false,
  setIsAskYogiAnywhereOpen: noop,
  toggleAskYogiAnywhere: noop,
  benchmarkingInsightUuid: null,
  benchmarkingInsightEntityName: null,
  benchmarkingInitialQuestion: null,
  openForBenchmarking: noop,
  exitBenchmarkingMode: noop,
  wikiProjectUuid: null,
  openForWiki: noop,
  exitWikiMode: noop,
  chartPage: null,
  setChartPage: noop,
  isOnChartsPage: false,
  setIsOnChartsPage: noop,
  isOnEmptyDashboard: false,
  setIsOnEmptyDashboard: noop,
  handedOffChart: null,
  chartPageSession: 0,
  chartUpdate: null,
  setChartUpdate: noop,
  clearChartUpdate: noop,
  setDashboardWrite: noop,
  isAskYogiWorking: false,
  setIsAskYogiWorking: noop,
  workingConversationId: null,
  setWorkingConversationId: noop,
  tileChatRequest: null,
  sendTileChat: noop,
  clearTileChatRequest: noop,
  tileConversations: {},
  setTileConversation: noop,
  getDashboardContextBuilder: () => null,
  pageSkills: null,
  reportSavedResources: noop,
}

export const useAskYogiAnywhere = (): AskYogiAnywhereContextType => {
  const context = useContext(AskYogiAnywhereContext)
  return context ?? defaultAskYogiAnywhereContext
}

/**
 * What the 2.1 dashboard on screen says the user is looking at. Its own
 * context, not a field of the wide value: React context has no selector, so a
 * selection change re-renders only what reads this.
 */
export const DashboardFocusContext = createContext<DashboardFocusChip | null>(
  null,
)

export const useDashboardFocus = (): DashboardFocusChip | null =>
  useContext(DashboardFocusContext)
